import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import router from '@/router'

// Cria uma instância do Axios com a configuração base
const api = axios.create({
  // CORREÇÃO: Uso de process.env (Webpack) em vez de import.meta.env (Vite)
  // Nota: A variável deve começar por VUE_APP_
  baseURL: process.env.VUE_APP_API_URL || 'https://localhost:7075/api',
  headers: {
    'Content-Type': 'application/json'
  }
})

// ============================================
// INTERCEPTOR DE LOADING GLOBAL (apiCallCount)
// ============================================
// Registrado PRIMEIRO para executar por último no request (mais externo)
// e primeiro no response (mais externo).
// Usa import estático — stores/ui.js não importa este módulo (usa axios direto),
// portanto não há circular dependency.
// ============================================
api.interceptors.request.use(
  (config) => {
    // Ignora requisições de polling/background que não devem acionar loading
    if (config._suppressLoading) return config
    const uiStore = useUiStore()
    uiStore.apiCallCount++
    return config
  },
  (error) => {
    // Se a requisição falhou antes de ser enviada, decrementa na mesma
    if (!error.config?._suppressLoading) {
      const uiStore = useUiStore()
      uiStore.apiCallCount = Math.max(0, uiStore.apiCallCount - 1)
    }
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    if (response.config._suppressLoading) return response
    const uiStore = useUiStore()
    uiStore.apiCallCount = Math.max(0, uiStore.apiCallCount - 1)
    return response
  },
  (error) => {
    if (error.config?._suppressLoading) return Promise.reject(error)
    const uiStore = useUiStore()
    uiStore.apiCallCount = Math.max(0, uiStore.apiCallCount - 1)
    return Promise.reject(error)
  }
)

// Interceptor de Requisição: Adiciona o Token automaticamente
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()

    // LEITURA FRESCA: Garante que estamos usando o token mais recente do LocalStorage
    // Isso resolve o problema de alterar o token manualmente no F12 e a Store não perceber
    const tokenFresh = localStorage.getItem('authToken')

    // Se houver token no LocalStorage, injeta no cabeçalho
    if (tokenFresh) {
      config.headers.Authorization = `Bearer ${tokenFresh}`

      // Sincroniza a store se o token da store estiver diferente do localStorage
      if (authStore.token !== tokenFresh) {
        authStore.token = tokenFresh
      }
    } else {
      // Se não houver token no LocalStorage mas a store achar que tem, limpa a store
      if (authStore.token) {
        authStore.logout()
      }
    }

    // GUARDIÃO: Injeta o contexto da empresa (RLS) para o backend
    // Fundamental para o AdminInterno conseguir carregar as listas do Tenant selecionado
    if (authStore.empresaAtiva) {
      config.headers.empresaAtiva = authStore.empresaAtiva
    }

    // GUARDIÃO: Injeta o contexto da organização ativa (multi-organização)
    // A empresa roteia o banco; a organização é o escopo de dado dentro do banco.
    if (authStore.organizacaoAtiva) {
      config.headers.organizacaoAtiva = authStore.organizacaoAtiva
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ============================================
// CAPTURA DE ERROS NO FRONTEND (Bugs)
// ============================================
// Envia erros 5xx e erros de runtime para o backend registrar no ErrorLog.
// Erros 4xx (controlados) NÃO são enviados — são validações do usuário.
// ============================================
let ultimaCaptura = null

function capturarErroFrontend (error, componente, rota) {
  try {
    const status = error?.response?.status

    // Apenas erros 5xx (bug) — 4xx são controlados
    if (status && status >= 400 && status < 500) {
      return
    }

    // Evita enviar o mesmo erro em duplicidade (rate-limit simples)
    const agora = Date.now()
    const chave = `${error?.message || ''}|${error?.config?.url || ''}|${status || ''}`
    if (ultimaCaptura && ultimaCaptura.chave === chave && agora - ultimaCaptura.ts < 5000) {
      return
    }
    ultimaCaptura = { chave, ts: agora }

    const responseData = error?.response?.data || {}

    // Fallback: o frontend registra 5xx mesmo que o backend já tenha registrado (duplicidade aceita).
    // O backend não expõe details/exceptionType no body por segurança (Opção B).

    const message = responseData.message || error?.message || 'Erro desconhecido no frontend'
    const details = responseData.details || ''
    const exceptionType = responseData.exceptionType || ''
    const stackTrace = error?.stack || ''
    let requestBody = error?.config?.data || ''

    if (typeof requestBody === 'object' && requestBody !== null) {
      try {
        requestBody = JSON.stringify(requestBody)
      } catch (e) {
        requestBody = ''
      }
    }

    const payload = {
      message,
      stackTrace,
      componenteFrontend: componente || 'api.js',
      rotaFrontend: rota || window.location.pathname,
      requestBody,
      // Novos campos para enriquecer o log no backend
      statusCode: status,
      url: error?.config?.url || '',
      details,
      exceptionType
    }

    // Dispara a requisição de forma assíncrona, sem bloquear o fluxo
    api.post('/ErrorLogs/frontend', payload).catch(() => {})
  } catch (e) {
    // Silêncio total — nunca deixar a captura de erro quebrar o fluxo
    console.warn('[ErrorLog] Falha ao capturar erro frontend:', e)
  }
}

// ============================================
// INTERCEPTOR DE RESPOSTA INTELIGENTE
// ============================================
// Gerencia 401 com renovação automática de token via refresh token.
// Se falhar a renovação, aí sim redireciona para o login.
// ============================================
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // O backend registra TODOS os 5xx via GlobalExceptionFilter (com detalhes técnicos).
    // O frontend só registra erros de runtime (sem resposta HTTP), chamados manualmente
    // pelos componentes Vue. 5xx HTTP não são registrados aqui para evitar duplicidade
    // e garantir que o ErrorLog receba a mensagem técnica real do backend.

    // Se não for 401, rejeita direto (não é erro de autenticação)
    if (error.response?.status !== 401) {
      return Promise.reject(error)
    }

    // Evita loop infinito: se a própria request de refresh-token der 401
    if (originalRequest.url === '/Auth/refresh-token') {
      console.warn('[API Interceptor] Refresh token expirado/inválido. Redirecionando para login...')
      const authStore = useAuthStore()
      authStore.limparSessaoLocal()
      if (router.currentRoute.value.name !== 'Login') {
        router.push({ name: 'Login', query: { sessaoExpirada: 'true' } })
      }
      return Promise.reject(error)
    }

    const authStore = useAuthStore()

    // GUARDIÃO: Se esta request já foi retentada uma vez e falhou de novo,
    // não tenta refresh novamente para evitar loop infinito.
    if (originalRequest._retry) {
      console.warn('[API Interceptor] Request já retentada falhou novamente (401). Deslogando...', originalRequest.url)
      authStore.limparSessaoLocal()
      if (router.currentRoute.value.name !== 'Login') {
        router.push({ name: 'Login', query: { sessaoExpirada: 'true' } })
      }
      return Promise.reject(error)
    }

    // Se já está refreshindo, enfileira esta request para retry
    if (authStore.isRefreshing) {
      return new Promise((resolve, reject) => {
        authStore.failedQueue.push({ resolve, reject })
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`
        return api(originalRequest)
      })
    }

    // Primeiro 401: inicia o refresh
    originalRequest._retry = true
    authStore.isRefreshing = true

    try {
      const newToken = await authStore.refreshAccessToken()

      // Processa a fila de requests pendentes
      authStore.processQueue(null, newToken)

      // Retry da request original
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return api(originalRequest)
    } catch (refreshError) {
      // Refresh falhou: processa fila com erro e desloga
      authStore.processQueue(refreshError, null)
      authStore.limparSessaoLocal()

      if (router.currentRoute.value.name !== 'Login') {
        router.push({ name: 'Login', query: { sessaoExpirada: 'true' } })
      }

      return Promise.reject(refreshError)
    } finally {
      authStore.isRefreshing = false
    }
  }
)

export { capturarErroFrontend }
export default api
